import { defineStore } from "pinia";
import { IFileTarget } from "../../types/FileManager/IFileTarget";
import type { FileOptions } from "@topol/image-picker";
import { api, LAMBDA_URL, lockedApi } from "@/api";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useRolesStore } from "@/stores/roles";

interface State {
  defaultPath: string;
  fileTarget: IFileTarget;
  imageMaxSize: number;
  customFileManager: boolean;
  enableFileManagerInGif: boolean;
  visibleFileManager: boolean;
  imageQualityJpeg: number;
  imageQualityPng: number;
  enableAutoResize: boolean;
  enableImageCompression: boolean;
  // for the case when multiple images are managed in one form, e.g. carousel
  fileIndex?: number;
  preferences?: {
    defaultTilesView?: boolean;
    hidePexelsIntegration?: boolean;
    maxUploadingFiles?: number;
  };
}

export const useFileManagerStore = defineStore("fileManger", {
  state: (): State => ({
    defaultPath: "/",
    visibleFileManager: false,
    customFileManager: false,
    enableFileManagerInGif: false,
    imageMaxSize: 2097152,
    imageQualityJpeg: 0.95,
    imageQualityPng: 0.8,
    enableAutoResize: true,
    enableImageCompression: true,

    /*
    File target is used to determine where to insert the image

    e.g. you open file manager for carousel block, and after upload, you
    want to insert the image into the precise MJML indexes
    */
    fileTarget: {
      sectionIndex: null,
      blockIndex: null,
      columnIndex: null,
      childIndex: null,
      prop: null,
    },
  }),

  getters: {
    getFileTarget(): IFileTarget | null {
      const props = Object.entries(this.fileTarget).filter((v) => v !== null);

      if (props.length < 1) {
        return null;
      }

      return this.fileTarget;
    },
    FMOptions(): FileOptions {
      const generalStore = useGeneralStore();
      const mjmlStore = useMjmlStore();
      const rolesStore = useRolesStore();
      return {
        imageMaxSize: this.imageMaxSize,
        urls: {
          folders: api.FOLDERS || "",
          lambda: LAMBDA_URL,
          imageUpload: api.IMAGE_UPLOAD || "",
          GCS_SIGNED_URL: lockedApi.GCS_SIGNED_URL,
        },
        type: generalStore.type,
        id: mjmlStore.templateId,
        userId: mjmlStore.authorize.userId,
        apiKey: mjmlStore.authorize.apiKey,
        imageCompressionOptions: this.enableImageCompression
          ? {
              qualityJpeg: this.imageQualityJpeg,
              qualityPng: this.imageQualityPng,
              maxWidth: mjmlStore.getBodyWidth,
              enableAutoResize: this.enableAutoResize,
            }
          : undefined,
        pexelsApiKey:
          "563492ad6f9170000100000101287a5183e34dd5994204a0494b7dca",
        light: generalStore.light,
        language: generalStore.language,
        preferences: this.preferences,
        disallowRemove: !rolesStore.permissions.canRemoveFiles,
      };
    },
  },

  actions: {
    setFileTarget(fileTarget: IFileTarget) {
      this.fileTarget = Object.assign({}, fileTarget);
    },

    setPreferences(preferences: State["preferences"]) {
      this.preferences = preferences;
    },

    getPreference(preference: string) {
      if (!this.preferences) {
        return undefined;
      }
      return this.preferences[preference as keyof State["preferences"]];
    },

    resetFileTarget() {
      this.fileTarget = {
        sectionIndex: null,
        blockIndex: null,
        columnIndex: null,
        childIndex: null,
        prop: null,
      };
    },

    setMaxImageSize(size: number) {
      this.imageMaxSize = size;
    },

    setCustomFileManager(value: boolean) {
      this.customFileManager = value;
    },

    setEnableFileManagerInGif(value: boolean) {
      this.enableFileManagerInGif = value;
    },

    showFileManager(visible: boolean) {
      if (this.customFileManager) {
        if (visible) {
          const message = {
            type: "callback",
            action: "onOpenFileManager",
            data: {},
          };
          return window.parent.postMessage(message, "*");
        }
      } else {
        this.visibleFileManager = visible;
      }
    },
  },
});
